// ** React Imports
import { useMemo } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Custom Hooks
import { useQueryWithLocationSearch } from "hooks/useQuery";

// ** Utils & Helpers
import { searchSexCustomSort } from "utils/searchSexCustomSort";

// ** Custom Filters
import { compute as filterLookingAgeCompute } from "hooks/filters/useFilterAgeSlider";
import useFilterSex, {
	compute as filterSexCompute,
} from "hooks/filters/useFilterSex";
import { compute as filterSearchSexCompute } from "hooks/filters/useFilterSearchSex";
import { compute as filterPreferencesCompute } from "hooks/filters/useFilterPreferences";
import { compute as filterAgeCompute } from "hooks/filters/useFilterAgeRange";
import { compute as filterContentCompute } from "hooks/filters/useFilterContent";
import { compute as filterPurposeCompute } from "hooks/filters/useFilterPurpose";
import { compute as filterLanguageCompute } from "hooks/filters/useFilterLanguage";
import { compute as filterHeightCompute } from "hooks/filters/useFilterHeight";
import { compute as filterFigureCompute } from "hooks/filters/useFilterFigure";
import { compute as filterZodiacCompute } from "hooks/filters/useFilterZodiac";
import { compute as filterHairCompute } from "hooks/filters/useFilterHair";
import { compute as filterRelationshipCompute } from "hooks/filters/useFilterRelationship";
import { compute as filterKidsCompute } from "hooks/filters/useFilterKids";
import { compute as filterDrinkingCompute } from "hooks/filters/useFilterDrinking";
import { compute as filterSmokingCompute } from "hooks/filters/useFilterSmoking";
import { compute as filterEducationCompute } from "hooks/filters/useFilterEducation";
import { compute as filterSortCompute } from "hooks/filters/useFilterSort";
import filterLocationCompute from "hooks/filters/compute/useFilterLocationCompute";

// #####################################################

const useUsersApiParams = ({
	viewType,
	locationSearch,
	notUseUserPrefferedSex,
	countryUrl,
	regionUrl,
	cityUrl,
	preferenceUrl,
	preferencesUrl,
}) => {
	// ** Odczytanie parametrów z query
	const query = useQueryWithLocationSearch(locationSearch);
	// **
	const userSearchSex = useSelector((state) => state.globalUser.searchSex);
	const userPrefferedSex = [...userSearchSex]
		.sort(searchSexCustomSort)
		.join("-");

	const initialFilterSex = useMemo(() => {
		if (query.sex) {
			return query.sex;
		} else if (notUseUserPrefferedSex) {
			return ""; //"woman-man-couple-transsexual";
		} else if (userPrefferedSex) {
			return userPrefferedSex;
		}
		return ""; //"woman-man-couple-transsexual";
	}, [notUseUserPrefferedSex, userPrefferedSex, locationSearch]);

	const filterSex = useFilterSex({
		initial: initialFilterSex,
		multiple: true,
	});

	const getApiParams = ({ isAuth }) => {
		let sortApiParam = filterSortCompute.urlToApiParam(query.sort);

		if (!isAuth && !query.sort) {
			sortApiParam = "special2";
		}

		return {
			sort: sortApiParam,
			page: Number(query.page) || 1,
			sex:
				query.sex || query.sex === ""
					? filterSexCompute(true).urlToApiParam(query.sex)
					: filterSexCompute(true).valueToApiParam(
							filterSex.selected,
						),
			sex_couple_1: filterSexCompute(true).urlToApiParam(
				query.sex_couple,
			),
			sex_couple_2: filterSexCompute(true).urlToApiParam(
				query.sex_couple_partner,
			),
			age_min: filterAgeCompute.urlToApiParam(query.age_from),
			age_max: filterAgeCompute.urlToApiParam(query.age_to),
			country: countryUrl,
			region: regionUrl,
			city: cityUrl,
			distance: query.distance
				? filterLocationCompute().distanceUrlToApiParam(query.distance)
				: null,
			only: filterContentCompute(true).urlToApiParam(query.only),
			s_sex: filterSearchSexCompute.urlToApiParam(query.who_search),
			s_age: filterLookingAgeCompute.urlToApiParam(query.who_search_age),
			s_for: filterPurposeCompute.searchForUrlToApiData(query.purpose),
			language: filterLanguageCompute.urlToApiParam(
				query.language_search,
			),
			height_min: filterHeightCompute.urlToApiParam(query.height_from),
			height_max: filterHeightCompute.urlToApiParam(query.height_to),
			figure: filterFigureCompute.urlToApiParam(query.figure_type),
			zodiac: filterZodiacCompute.urlToApiParam(query.zodiac),
			hair: filterHairCompute.urlToApiParam(query.hair),
			relationship: filterRelationshipCompute.urlToApiParam(
				query.relationship,
			),
			kids: filterKidsCompute.urlToApiParam(query.kids),
			drinking: filterDrinkingCompute.urlToApiParam(query.drinking),
			smoking: filterSmokingCompute.urlToApiParam(query.smoking),
			education: filterEducationCompute.urlToApiParam(query.education),
			preference:
				preferenceUrl || preferencesUrl
					? filterPreferencesCompute.urlToApiParam(
							preferenceUrl || preferencesUrl,
						)
					: query.preferences
						? filterPreferencesCompute.queryUrlToApiParam(
								query.preferences,
							)
						: null,
			name: query.nick ?? null,
			view: viewType,
			limit:
				isAuth && viewType === "list"
					? 20
					: isAuth && viewType === "grid"
						? 40
						: 20,
		};
	};

	// #####################################################

	return { getApiParams };
};

// #####################################################

export default useUsersApiParams;

// #####################################################
